import type { JurisdictionRow } from '@mollusk/shared';

/**
 * Pure CSV serializer for the Permit Detail "Download CSV" button. Exports
 * *every* jurisdiction with *every* column — all scalar `JurisdictionRow`
 * fields, plus the nested `trade_counts` / `tag_counts` maps fanned out into
 * one `trade_<value>` / `tag_<value>` column each (wide format). A cell holds
 * the permit count for that trade/tag, or blank when the jurisdiction has none.
 *
 * Kept free of the DOM (no Blob/anchor) so it can be unit-tested; the component
 * wraps the returned string in a download.
 */

/** The two nested map fields are fanned out into wide columns, not emitted raw. */
const MAP_FIELDS = ['trade_counts', 'tag_counts'] as const;

function escapeField(v: string): string {
  // Quote when the value could break CSV structure; double embedded quotes.
  return /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** Scalar cell → CSV text. null/undefined → '', booleans → true/false. */
function scalar(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return escapeField(String(v));
}

/** Base scalar columns, in the row's own key order, minus the fanned-out maps. */
function baseColumns(rows: readonly JurisdictionRow[]): string[] {
  const first = rows[0];
  if (!first) return [];
  return Object.keys(first).filter((k) => !MAP_FIELDS.includes(k as (typeof MAP_FIELDS)[number]));
}

/** Sorted union of keys seen across a nested map field (deterministic column order). */
function unionKeys(rows: readonly JurisdictionRow[], field: (typeof MAP_FIELDS)[number]): string[] {
  const keys = new Set<string>();
  for (const r of rows) {
    for (const k of Object.keys(r[field])) keys.add(k);
  }
  return [...keys].sort((a, b) => a.localeCompare(b));
}

/**
 * Build the full CSV text for `rows`. The caller passes the complete jurisdiction
 * list (not the filtered view) so the export always "gets everything".
 */
export function buildJurisdictionsCsv(rows: readonly JurisdictionRow[]): string {
  const base = baseColumns(rows);
  const trades = unionKeys(rows, 'trade_counts');
  const tags = unionKeys(rows, 'tag_counts');

  const header = [...base, ...trades.map((t) => `trade_${t}`), ...tags.map((t) => `tag_${t}`)].map(
    escapeField,
  );

  const lines = [header.join(',')];
  for (const row of rows) {
    const r = row as unknown as Record<string, unknown>;
    const cells = [
      ...base.map((k) => scalar(r[k])),
      ...trades.map((t) => scalar(row.trade_counts[t] ?? '')),
      ...tags.map((t) => scalar(row.tag_counts[t] ?? '')),
    ];
    lines.push(cells.join(','));
  }
  // Trailing newline: friendlier to `wc -l` and POSIX text tools.
  return `${lines.join('\n')}\n`;
}
